import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone, Inbox, CalendarClock, CalendarDays, Crown, LayoutGrid, Flame, Percent, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import HisobAppPreview from '@/components/marketing/HisobAppPreview';
import {
  useActiveMarketingItems, useMarketingApplications, useMarketingCampaigns, useCashbackOffers,
} from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, applicationStatusLabel, applicationStatusVariant, campaignStatusLabel,
  campaignStatusVariant, formatPeriod, promotionLabel,
  type ApplicationStatus, type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

export default function AdminMarketingDashboard() {
  const { t } = useTranslation();
  const { data: campaigns = [] } = useMarketingCampaigns();
  const { data: applications = [] } = useMarketingApplications({ adminView: true, per_page: 200 });
  const { data: offers = [] } = useCashbackOffers({ adminView: true });
  const { data: activeItems = [] } = useActiveMarketingItems();

  const pending = applications.filter((a: any) => ['SUBMITTED', 'UNDER_REVIEW'].includes(a.status));
  const activeCampaigns = campaigns.filter((c: any) => c.status === 'ACTIVE');
  const scheduled = campaigns.filter((c: any) => c.status === 'SCHEDULED');
  const completed = campaigns.filter((c: any) => ['COMPLETED', 'CANCELLED'].includes(c.status));
  const pendingOffers = offers.filter((o: any) => o.status === 'SUBMITTED');
  const endingSoon = activeCampaigns.filter((c: any) => {
    const d = (new Date(c.end_at).getTime() - Date.now()) / 86400000;
    return d > 0 && d <= 7;
  });

  const itemsByType = (pt: PromotionType) => (activeItems as any[]).filter((i) => i.promotion_type === pt);

  const kpis = [
    { label: t('mkt_dashboard.kpiActive'), value: (activeItems as any[]).length, icon: Megaphone, to: '/admin/marketing/campaigns' },
    { label: t('mkt_dashboard.kpiScheduled'), value: scheduled.length, icon: CalendarDays, to: '/admin/marketing/campaigns' },
    { label: t('mkt_dashboard.kpiPending'), value: pending.length, icon: Inbox, to: '/admin/marketing/applications' },
    { label: t('mkt_dashboard.kpiVip'), value: itemsByType('VIP_STORES').length, icon: Crown, to: '/admin/marketing/placements/vip' },
    { label: t('mkt_dashboard.kpiCategories'), value: itemsByType('PROMOTED_CATEGORIES').length, icon: LayoutGrid, to: '/admin/marketing/placements/categories' },
    { label: t('mkt_dashboard.kpiPopular'), value: itemsByType('POPULAR_STORES').length, icon: Flame, to: '/admin/marketing/placements/popular' },
    { label: t('mkt_dashboard.kpiBigCashback'), value: itemsByType('BIG_CASHBACK').length, icon: Percent, to: '/admin/marketing/placements/big-cashback' },
    { label: t('mkt_dashboard.kpiEnding'), value: endingSoon.length, icon: CalendarClock, to: '/admin/marketing/calendar' },
  ];

  const section = (title: string, rows: any[], empty: string) => (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border p-4">
        <h2 className="text-sm font-medium text-foreground">{title} ({rows.length})</h2>
      </div>
      {rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="divide-y divide-border">
          {rows.slice(0, 6).map((c: any) => (
            <Link key={c.id} to={`/admin/marketing/campaigns/${c.id}`} className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/30">
              <div>
                <p className="text-sm font-medium text-foreground">{c.name}</p>
                <p className="text-xs text-muted-foreground">
                  {promotionLabel[c.promotion_type as PromotionType]} · {formatPeriod(c.start_at, c.end_at)}
                </p>
              </div>
              <Badge variant={campaignStatusVariant[c.status as CampaignStatus]}>
                {campaignStatusLabel[c.status as CampaignStatus]}
              </Badge>
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('mkt_dashboard.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('mkt_dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild><Link to="/admin/marketing/campaigns/new"><Plus size={16} className="mr-1" /> {t('mkt_dashboard.create')}</Link></Button>
          <Button asChild variant="outline"><Link to="/admin/marketing/applications">{t('mkt_dashboard.viewApplications')}</Link></Button>
          <Button asChild variant="outline"><Link to="/admin/marketing/calendar">{t('mkt_dashboard.calendar')}</Link></Button>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Link key={k.label} to={k.to} className="rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
            <div className="mb-2 flex items-center gap-2 text-muted-foreground">
              <k.icon size={16} />
              <span className="text-xs">{k.label}</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{k.value}</p>
          </Link>
        ))}
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border p-4">
            <h2 className="text-sm font-medium text-foreground">{t('mkt_dashboard.pendingApplications')} ({pending.length})</h2>
            {pendingOffers.length > 0 && (
              <Link to="/admin/marketing/cashback" className="text-xs text-primary hover:underline">
                {t('mkt_dashboard.cashbackOffers', { n: pendingOffers.length })}
              </Link>
            )}
          </div>
          {pending.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_dashboard.noPending')}</p>
          ) : (
            <div className="divide-y divide-border">
              {pending.slice(0, 6).map((a: any) => (
                <Link key={a.id} to="/admin/marketing/applications" className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/30">
                  <div>
                    <p className="text-sm font-medium text-foreground">{a.merchant?.name ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.application_no} · {promotionLabel[a.promotion_type as PromotionType]}
                    </p>
                  </div>
                  <Badge variant={applicationStatusVariant[a.status as ApplicationStatus]}>
                    {applicationStatusLabel[a.status as ApplicationStatus]}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </div>

        {section(t('mkt_dashboard.secActive'), activeCampaigns, t('mkt_dashboard.secActiveEmpty'))}
        {section(t('mkt_dashboard.secUpcoming'), scheduled, t('mkt_dashboard.secUpcomingEmpty'))}
        {section(t('mkt_dashboard.secEnding'), endingSoon, t('mkt_dashboard.secEndingEmpty'))}
        {section(t('mkt_dashboard.secCompleted'), completed, t('mkt_dashboard.secCompletedEmpty'))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {PROMOTION_TYPES.map((pt) => (
          <HisobAppPreview
            key={pt}
            promotionType={pt}
            items={itemsByType(pt).map((i) => ({
              id: i.item_id ?? i.id,
              merchant_name: i.merchant_name,
              category_name: i.category_name,
              promo_title: i.promo_title,
              promo_subtitle: i.promo_subtitle,
              promo_image_url: i.promo_image_url,
              cashback_bps: i.cashback_bps,
              display_order: i.display_order,
            }))}
          />
        ))}
      </div>
    </div>
  );
}
